import {
  WasteScheduleAdherenceStatus,
  WasteServiceKind,
  WasteServiceScheduleType,
} from "generated-graphql/graphql";
import { formatInvoiceDate, NO_VALUE } from "routes/Customer/Waste/Invoices/utils";

export const STATUS_LABELS = {
  [WasteScheduleAdherenceStatus.WithinSchedule]: "Within schedule",
  [WasteScheduleAdherenceStatus.OutsideSchedule]: "Outside schedule",
  [WasteScheduleAdherenceStatus.UnscheduledService]: "Unscheduled service",
  [WasteScheduleAdherenceStatus.NoServiceShown]: "No service shown",
  [WasteScheduleAdherenceStatus.NeedsReview]: "Can't compare",
} satisfies Record<WasteScheduleAdherenceStatus, string>;

export const KIND_LABELS = {
  [WasteServiceKind.WastePickup]: "Retail pickup",
  [WasteServiceKind.VacuumSump]: "Vacuum sump",
  [WasteServiceKind.PressureTreatedWood]: "Pressure-treated wood",
  [WasteServiceKind.SpecialCollection]: "Special collection",
  [WasteServiceKind.SpillResponse]: "Spill response",
  [WasteServiceKind.SupplyDelivery]: "Supply delivery",
  [WasteServiceKind.EquipmentRental]: "Equipment rental",
  [WasteServiceKind.Sampling]: "Sampling",
  [WasteServiceKind.Other]: "Other",
} satisfies Record<WasteServiceKind, string>;

// Filter options arrive as the raw omnisearch string, so these look-ups are
// keyed by string rather than by the enum (see util/filterOptionLabels.ts).
const STATUS_LABEL_BY_VALUE = new Map<string, string>(Object.entries(STATUS_LABELS));
const KIND_LABEL_BY_VALUE = new Map<string, string>(Object.entries(KIND_LABELS));

/** The status label for a filter option; the raw value when it names no status. */
export const statusOptionLabel = (value: string): string =>
  STATUS_LABEL_BY_VALUE.get(value) ?? value;

/** The service label for a filter option; the raw value when it names no service. */
export const kindOptionLabel = (value: string): string => KIND_LABEL_BY_VALUE.get(value) ?? value;

/**
 * Shown beside every amount, so a money column next to a late visit is not read
 * as an overcharge.
 */
export const ASSOCIATED_BILLING =
  "What these results were billed, never an overcharge: the audit compares service against the schedule, not against a rate.";

/** The invoice's own evidence did not resolve to a kind. */
export const UNKNOWN_KIND_LABEL = "Unclear";

/**
 * What a charge was read as, noting sump work where the charge states it: a
 * pickup that serviced the sump is graded as a pickup, and the note is the
 * only place that says the sump was done.
 */
export function serviceLabel(
  kind: WasteServiceKind | null | undefined,
  serviceSump: boolean | null | undefined
): string {
  const label = kind ? KIND_LABELS[kind] : UNKNOWN_KIND_LABEL;
  return serviceSump ? `${label}, sump serviced` : label;
}

/** Our name for a service with the vendor's own term beside it, where the source printed one. */
export function withVendorTerm(label: string, term: string | null | undefined): string {
  return term ? `${label} \u00b7 ${term}` : label;
}

/**
 * How far outside the window, read as English. A window is a range, so "2 days
 * late" means two days after it closed rather than after any particular date.
 */
export function formatDaysOutside(daysOutside: number | null | undefined): string {
  if (daysOutside == null) return NO_VALUE;
  if (daysOutside === 0) return "On time";
  const magnitude = Math.abs(daysOutside);
  const days = magnitude === 1 ? "1 day" : `${magnitude} days`;
  return daysOutside < 0 ? `${days} early` : `${days} late`;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const MONDAY = 1;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Read as the calendar day it spells, in the zone the schedule states it in. */
function utcDay(value: string): Date | null {
  const day = value.slice(0, 10);
  if (!ISO_DAY.test(day)) return null;
  const parsed = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * A vendor week named by its last day: Republic publishes a week-ending date
 * and the Monday is derived, so naming it by the Monday states a commitment the
 * vendor never made.
 */
function namedByLastDay(start: string, end: string): boolean {
  const from = utcDay(start);
  const through = utcDay(end);
  if (from == null || through == null || from.getUTCDay() !== MONDAY) return false;
  const span = through.getTime() - from.getTime();
  return span >= 0 && span < WEEK_MS;
}

/** The window a vendor published: a week for some, a range or a deadline for others. */
export function formatWindow(
  start: string | null | undefined,
  end: string | null | undefined
): string {
  if (!end) return "Not stated";
  if (!start) return `By ${formatInvoiceDate(end)}`;
  if (namedByLastDay(start, end)) return `Week ending ${formatInvoiceDate(end)}`;
  return `${formatInvoiceDate(start)} – ${formatInvoiceDate(end)}`;
}

/**
 * The days the audit graded against, for a window whose name does not state
 * them. Null wherever the name already does, so nothing is said twice.
 */
export function formatGradedSpan(
  start: string | null | undefined,
  end: string | null | undefined
): string | null {
  if (!start || !end || !namedByLastDay(start, end)) return null;
  return `${formatInvoiceDate(start)} – ${formatInvoiceDate(end)}`;
}

/**
 * Facility name with the customer's identifier in brackets, either alone when
 * the other is unknown — the shape the invoice audit prints.
 */
export function facilityLabel(
  facilityName: string | null | undefined,
  customerFacilityId: string | null | undefined
): string | null {
  if (facilityName && customerFacilityId) return `${facilityName} (${customerFacilityId})`;
  return facilityName || customerFacilityId || null;
}

export const SCHEDULE_TYPE_LABELS = {
  [WasteServiceScheduleType.Regular]: "Regular",
  [WasteServiceScheduleType.OnRequest]: "On request",
  [WasteServiceScheduleType.Urgent]: "Urgent",
  [WasteServiceScheduleType.Emergency]: "Emergency",
  [WasteServiceScheduleType.Rework]: "Rework",
} satisfies Record<WasteServiceScheduleType, string>;

/** The arrangements a visit's charges state, in the order they first appear. */
export function statedScheduleTypes(
  lines: readonly { scheduleType?: WasteServiceScheduleType | null }[]
): WasteServiceScheduleType[] {
  return [...new Set(lines.flatMap((line) => (line.scheduleType ? [line.scheduleType] : [])))];
}

/**
 * The vendor's name for a service where it says more than ours: Clean Earth's
 * "Retail" beside "Retail pickup" says nothing new.
 */
export function vendorTermAddsDetail(label: string, term: string | null | undefined): boolean {
  // Compared on letters and digits alone: "Pressure Treated Wood" says nothing
  // "Pressure-treated wood" does not.
  const bare = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
  return term != null && bare(term) !== "" && !bare(label).includes(bare(term));
}

/** Month and day alone, for a date whose year is stated beside it. */
export const monthDay = (value: string) =>
  new Date(`${value.slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

/**
 * The service dates a visit's charges give, for a visit with no single one: a
 * visit is dated only when every charge names the same day, so this says which
 * half of that failed.
 */
export function describeChargeDates(lines: readonly { serviceDate?: string | null }[]): string {
  // Compared as calendar days, the unit the audit dates a visit in.
  const days = [
    ...new Set(lines.flatMap((line) => (line.serviceDate ? [line.serviceDate.slice(0, 10)] : []))),
  ];
  days.sort();
  const undated = lines.filter((line) => !line.serviceDate).length;
  const missing = undated === 0 ? null : `${undated} of ${lines.length} charges give none`;
  const [first] = days;
  const last = days.at(-1);
  if (first == null || last == null) return "No charge gives a date";
  const stated =
    days.length === 1
      ? formatInvoiceDate(first)
      : `${days.length} dates, ${first.slice(0, 4) === last.slice(0, 4) ? monthDay(first) : formatInvoiceDate(first)} – ${formatInvoiceDate(last)}`;
  return missing == null ? stated : `${stated}; ${missing}`;
}
